// apps/web/src/widgets/per-option-detail-card/renderer.tsx
//
// Phase 11.5 — REND-01 PerOptionDetailCardRenderer. Composes onto a sibling
// option-set widget via config.optionSetWidgetId (the FK across widget instances
// in the same recipe — mirrors ranked-tier/renderer.tsx). For each SELECTED
// source option it renders one Card (title = the SOURCE option's label) whose
// sections are the config.sections entries whose categoryId is in that option's
// categoryIds[]. Each section resolves its categoryId against
// recipe.optionCategories → OptionCategory.blocks[] and renders the category's
// blocks by `kind`. When config.perItemCheckboxes, each rendered line is a Radix
// Checkbox + label; toggling writes a composite optionId::sectionId::blockId id
// into checkedItems (auto-persisted by the 11.3 provider); a checked line gets
// line-through + a Printer glyph button.
//
// State shape: { checkedItems: ReadonlyArray<string> } (Plan 01 type-map — the
// bare-checkedItems variant, disjoint from summary-checklist's { checkedItems,
// viewMode }). This renderer blends ranked-tier's compose/guard/shell skeleton
// (read the source option-set `selected` via the engine hook) with
// summary-checklist's checkbox/immutable-array-toggle idiom.
//
// LOAD-BEARING SHAPE-ONLY CONSTRAINT (RESEARCH §"Critical Architectural
// Finding"): OptionCategoryBlock carries field DESCRIPTORS, not per-option
// VALUES. In v1 there are NO per-option values anywhere in the data model:
//   - single-value declares { label, valueType, optional } — NO value field.
//   - list / nested-picker declare fields:[{name,type,required}] — NO items.
//   - template-driven declares { templateId, config: unknown } — config is NOT
//     rendered (v1 placeholder).
// So v1 renders block STRUCTURE only: the block label, the list/nested-picker
// field NAMES, the nested-picker "choose {min}-{max}" qualifier from pickCount,
// and the template-driven placeholder string. It NEVER reads a populated
// per-option value field off a block or option — that data does not exist.
// Variant-override + scaled-amount content is Fork 1 → Phase 11.6 (which wires
// real per-option data into this card). Author config sub-forms (Fork 2) remain
// OUT. The `@media print` wiring landed in Phase 11.8 (Plan 05): each
// print-included line carries a data-print-included attribute, the on-screen
// Printer glyph is `.no-print`, and a `.print-only` UNCONDITIONAL empty checkbox
// is emitted per print-included line — it NEVER inherits the on-screen pressed
// state (a fresh hand-tick checklist on the printed page; the LOAD-BEARING
// print-inclusion contract).
//
// Compose discipline (Rules of Hooks — PATTERNS §SP-5): the source-state hook is
// called ONCE at the top level on config.optionSetWidgetId, never per-option
// (the single call site below is the only non-import reference in this file).
// The source options[] are read via the shared pure readSourceOptions helper
// (Plan 01) — NOT re-derived inline.
//
// C1 generality (scripts/grep-no-recipe-identifiers.sh): the renderer names NO
// category and hardcodes NO recipe vocabulary — section labels, block labels,
// and field names all flow from config / recipe.optionCategories DATA. Blocks are
// resolved generically by `kind`, never by a domain name.
//
// XSS discipline (threat T-11.5.03-XSS): every label / field name is rendered as
// a JSX text child (React auto-escapes). There is NO dangerouslySetInnerHTML and
// no raw-HTML / markdown path; the template-driven block's `config: unknown` is
// explicitly NOT rendered. Mirrors the branch-variant text-child precedent.

import type {
  OptionCategory,
  OptionCategoryBlock,
  OptionSetConfig,
} from "@the-recipe-spread/shared";
import { Printer } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useOtherWidgetState } from "../use-other-widget-state.js";
import { readSourceOptions } from "../use-source-selection.js";
import type { WidgetRendererProps } from "../widget-renderer-props.js";

// Composite-id delimiter (UI-SPEC-resolved): a line's stable id is
// `${optionId}::${sectionId}::${blockId}` (+ `::${itemIndex}` for list rows).
const DELIM = "::";

// Shared Card-shell + heading fallback used by the missing-source graceful-
// degrade branch + the nothing-selected header floor. Byte-identical copy +
// typography to ranked-tier/renderer.tsx:94-130 so the renderers degrade the
// same way.
function FallbackShell({
  labelId,
  heading,
  message,
}: {
  labelId: string;
  heading: string;
  message: string;
}): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-4">
        <section aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {heading}
          </h3>
          <p className="text-sm text-[var(--recipe-muted)]">{message}</p>
        </section>
      </CardContent>
    </Card>
  );
}

export function PerOptionDetailCardRenderer({
  widget,
  config,
  recipe,
  state,
  onStateChange,
}: WidgetRendererProps<"per-option-detail-card">): React.ReactElement {
  // Own interactive state — the composite-id set of ticked lines.
  const checked: ReadonlyArray<string> = state?.checkedItems ?? [];

  // Print-inclusion intent is render-side, component-local state (NOT persisted,
  // NOT modeled in WidgetState). The Printer glyph toggles membership here; the
  // VISUAL print styling (`@media print`) is Phase 11.8 — this only records
  // intent + flips aria-pressed. A line can only be print-included while checked.
  const [printIncluded, setPrintIncluded] = useState<ReadonlyArray<string>>([]);

  // Compose: read the source option-set's `selected` ids via the engine hook
  // (ONE top-level call — Rules of Hooks). Source state lives in the provider
  // (mutable); source options live in the recipe (immutable post-validation).
  const sourceState = useOtherWidgetState(config.optionSetWidgetId);
  const rawSelectedIds: ReadonlyArray<string> =
    sourceState !== undefined && "selected" in sourceState ? sourceState.selected : [];
  // WR-01: dedupe (first-occurrence order) before the iteration below. The
  // route gate (WidgetInteractionStateSchema option-set variant) does NOT
  // enforce uniqueness on `selected`, so a crafted/legacy persisted blob can
  // feed duplicate ids; mapping them directly would emit duplicate React keys,
  // duplicate data-option-id, and (with perItemCheckboxes) duplicate Checkbox
  // id/htmlFor pairs. Immutable — a new array; sourceState.selected is untouched.
  const selectedIds: ReadonlyArray<string> = Array.from(new Set(rawSelectedIds));

  // Source-option lookup via the shared pure helper (Plan 01). null = source
  // widget missing / wrong templateId / non-array options → graceful fallback.
  const sourceOptions = readSourceOptions(recipe.widgets ?? [], config.optionSetWidgetId);

  const labelId = `${widget.instanceId}-label`;

  // Immutable whole-state replace (PATTERNS §SP-3 array form, dropping viewMode).
  const toggle = (lineId: string): void => {
    onStateChange({
      checkedItems: checked.includes(lineId)
        ? checked.filter((x) => x !== lineId)
        : [...checked, lineId],
    });
  };

  // Print-inclusion toggle — component-local only (no onStateChange, no persist).
  const togglePrint = (lineId: string): void => {
    setPrintIncluded((prev) =>
      prev.includes(lineId) ? prev.filter((x) => x !== lineId) : [...prev, lineId],
    );
  };

  // A single rendered line: its accessible label text + the composite id. When
  // perItemCheckboxes is on it is a Checkbox + <label htmlFor> association +
  // (when checked) a Printer print-inclusion glyph button; otherwise a plain
  // text line. Strikethrough is REDUNDANT to the authoritative Radix
  // data-[state=checked] signal (never convey "done" by color alone).
  const renderLine = (lineId: string, text: string, secondary?: string): React.ReactElement => {
    const isChecked = checked.includes(lineId);
    if (!config.perItemCheckboxes) {
      return (
        <span className="flex items-baseline gap-2 text-sm text-[var(--recipe-ink)]">
          <span>{text}</span>
          {secondary !== undefined ? (
            <span className="text-xs text-[var(--recipe-muted)]">{secondary}</span>
          ) : null}
        </span>
      );
    }
    const isPrintIncluded = printIncluded.includes(lineId);
    return (
      // 11.8 — the line carries data-print-included so the @media print CSS can
      // key on the print-inclusion intent. The on-screen Printer glyph is
      // .no-print (suppressed on the printed page); a .print-only UNCONDITIONAL
      // empty checkbox is emitted for print-included lines — it is NEVER bound to
      // isPrintIncluded/aria-pressed (a fresh hand-tick box, not a snapshot of the
      // on-screen state).
      <span
        className="flex items-center gap-2"
        data-print-included={isPrintIncluded ? "true" : undefined}
      >
        {/* .print-only unconditional empty checkbox — shown only under @media
            print, only for print-included lines, NEVER bound to on-screen state. */}
        {isPrintIncluded ? (
          <span
            aria-hidden
            className="print-only inline-block h-3 w-3 shrink-0 border border-[var(--recipe-ink)]"
          />
        ) : null}
        <Checkbox
          id={lineId}
          checked={isChecked}
          onCheckedChange={() => {
            toggle(lineId);
          }}
          className="no-print"
        />
        <label
          htmlFor={lineId}
          className={`flex items-baseline gap-2 text-sm ${
            isChecked ? "line-through text-[var(--recipe-muted)]" : "text-[var(--recipe-ink)]"
          }`}
        >
          <span>{text}</span>
          {secondary !== undefined ? (
            <span className="text-xs text-[var(--recipe-muted)]">{secondary}</span>
          ) : null}
        </label>
        {isChecked ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Toggle print inclusion for this item"
            aria-pressed={isPrintIncluded}
            onClick={() => {
              togglePrint(lineId);
            }}
            className={`no-print ${isPrintIncluded ? "text-[var(--recipe-accent)]" : "text-[var(--recipe-muted)]"}`}
          >
            <Printer size={14} aria-hidden />
          </Button>
        ) : null}
      </span>
    );
  };

  // Render ONE OptionCategoryBlock by `kind` — STRUCTURE only (see the LOAD-
  // BEARING constraint in the header). `idBase` = `${optionId}::${sectionId}`.
  const renderBlock = (block: OptionCategoryBlock, idBase: string): React.ReactElement => {
    const blockLineId = `${idBase}${DELIM}${block.id}`;
    switch (block.kind) {
      case "single-value":
        // Label only — there is NO scalar value in v1.
        return (
          <li key={block.id} className="flex flex-col gap-1">
            {renderLine(blockLineId, block.label)}
          </li>
        );
      case "list":
        // Label header + the field NAMES (the row SHAPE) — there are NO items.
        // Each field name is its own checkbox-eligible line (append ::index).
        return (
          <li key={block.id} className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-[var(--recipe-muted)]">{block.label}</span>
            <ul className="ml-3 flex flex-col gap-1">
              {block.fields.map((field, i) => (
                <li key={field.name}>{renderLine(`${blockLineId}${DELIM}${i}`, field.name)}</li>
              ))}
            </ul>
          </li>
        );
      case "nested-picker":
        // Label + the generic "choose {min}-{max}" qualifier from pickCount +
        // the field NAMES. NO concrete entries to pick from in v1.
        return (
          <li key={block.id} className="flex flex-col gap-1">
            {renderLine(
              blockLineId,
              block.label,
              `choose ${block.pickCount.min}–${block.pickCount.max}`,
            )}
            <ul className="ml-3 flex flex-col gap-1">
              {block.fields.map((field, i) => (
                <li key={field.name}>
                  {renderLine(`${blockLineId}${DELIM}np${DELIM}${i}`, field.name)}
                </li>
              ))}
            </ul>
          </li>
        );
      default:
        // template-driven → v1 placeholder; the `config` is NEVER rendered.
        return (
          <li key={block.id}>
            <span className="text-sm text-[var(--recipe-muted)]">
              Embedded view not available in this version.
            </span>
          </li>
        );
    }
  };

  // (1) Source missing/malformed → fallback (byte-identical to ranked-tier:125).
  if (sourceOptions === null) {
    return (
      <FallbackShell
        labelId={labelId}
        heading={widget.name}
        message="Source option-set widget missing."
      />
    );
  }

  // (2) Source present but nothing selected → header floor (the muted hint).
  if (selectedIds.length === 0) {
    return (
      <FallbackShell
        labelId={labelId}
        heading={widget.name}
        message="Select options to see details."
      />
    );
  }

  // (3) Populated. Iterate SELECTED source options (in source-selection order);
  // for each, resolve the source option (silent-skip if absent — mirror
  // ranked-tier's misconfigured drop at :147-153) and render one Card. Its
  // sections are the config.sections whose categoryId is in that option's
  // categoryIds[]; each section resolves its categoryId against
  // recipe.optionCategories (silent-skip an unresolved id).
  return (
    <div className="flex flex-col gap-4">
      {selectedIds.map((optionId) => {
        const sourceOption: OptionSetConfig["options"][number] | undefined = sourceOptions.find(
          (o) => o.id === optionId,
        );
        if (sourceOption === undefined) return null;

        const optionCategoryIds = sourceOption.categoryIds ?? [];
        const cardLabelId = `${widget.instanceId}-${optionId}-label`;

        const visibleSections = config.sections.filter((section) =>
          optionCategoryIds.includes(section.categoryId),
        );

        return (
          <Card key={optionId} data-option-id={optionId}>
            <CardContent className="p-4">
              <section aria-labelledby={cardLabelId} className="flex flex-col gap-4">
                <h3
                  id={cardLabelId}
                  className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
                >
                  {sourceOption.label}
                </h3>
                {visibleSections.map((section) => {
                  // Resolve the section's categoryId → recipe.optionCategories.
                  // An unresolved id silently skips the section (graceful-degrade,
                  // no crash, no error copy) — mirror summary-checklist:240.
                  const category: OptionCategory | undefined = recipe.optionCategories?.find(
                    (c) => c.id === section.categoryId,
                  );
                  if (category === undefined) return null;

                  const secLabelId = `${widget.instanceId}-${optionId}-${section.id}-label`;
                  const idBase = `${optionId}${DELIM}${section.id}`;

                  return (
                    // biome-ignore lint/a11y/useSemanticElements: section role="group" matches the Phase 6/11.4 widget-grouping convention; a native <fieldset> would inject legend styling and constrain the block layout.
                    <section
                      key={section.id}
                      role="group"
                      aria-labelledby={secLabelId}
                      className="flex flex-col gap-2"
                    >
                      <h4
                        id={secLabelId}
                        className="text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]"
                      >
                        {section.label}
                      </h4>
                      <ul className="flex flex-col gap-2">
                        {category.blocks.map((block) => renderBlock(block, idBase))}
                      </ul>
                    </section>
                  );
                })}
              </section>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
